"use client";
import { useAppContext } from "@/providers/AppProvider";
import { IExperience } from "@/types";
import {
    formattedEndDate,
    formattedStartDate,
    getTotalDuration,
} from "@/utils/formateDate";
import { sortedData } from "@/utils/sort";
import { motion } from "framer-motion";
import { Briefcase } from "lucide-react";
import Link from "next/link";
import { useEffect, useRef } from "react";

const ExperienceTimeline = () => {
    const { experiences } = useAppContext();
    const experienceRef = useRef<HTMLElement | null>(null);

    // Scrolling to section if url have hash
    useEffect(() => {
        const hash = window.location.hash;
        if (hash === "#experience") {
            if (experienceRef.current) {
                experienceRef.current.scrollIntoView({
                    behavior: "smooth",
                    block: "start",
                });
            }
        }
    }, []);

    return (
        <motion.section
            ref={experienceRef}
            id="experience"
            className="pt-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
                hidden: {},
                visible: {
                    transition: {
                        staggerChildren: 0.2,
                    },
                },
            }}
        >
            <motion.h1
                className="mb-4 text-2xl font-bold lg:text-3xl"
                variants={{
                    hidden: { opacity: 0, y: 20 },
                    visible: { opacity: 1, y: 0 },
                }}
            >
                <Link href="#experience" className="flex items-center gap-4">
                    <Briefcase className="h-[2rem] w-[2rem] text-primary" />
                    Experience
                </Link>
            </motion.h1>
            <div className="ml-14 flex flex-col items-start">
                {experiences &&
                    sortedData(experiences).map((experience, index) => (
                        <ExperienceTimelineItem
                            key={index}
                            experience={experience}
                            isFirstItem={index === 0}
                            isLastItem={index === experiences?.length - 1}
                        />
                    ))}
            </div>
        </motion.section>
    );
};

export default ExperienceTimeline;

const ExperienceTimelineItem = ({
    isFirstItem,
    experience,
    isLastItem,
}: {
    isFirstItem: boolean;
    experience: IExperience;
    isLastItem: boolean;
}) => {
    const { role, company, employmentType, startDate, endDate, descriptions } =
        experience;

    return (
        <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={{
                hidden: { opacity: 0, x: -20 },
                visible: { opacity: 1, x: 0 },
            }}
            transition={{ duration: 0.4 }}
            className={`relative ${!isLastItem ? "pb-8" : ""}`}
        >
            {isFirstItem && (
                <div className="absolute -top-4 left-0 h-1/5 w-[1px] transform bg-border-color" />
            )}
            {!isLastItem && (
                <div className="absolute left-0 top-2 h-full w-[1px] -translate-x-1/2 transform bg-border-color" />
            )}
            <div className="absolute left-0 top-2 h-3 w-3 -translate-x-1/2 transform rounded-full bg-primary" />
            <div className="ml-4">
                <h2 className="text-lg font-semibold">{role}</h2>
                <p className="text-sm font-medium text-primary">
                    {`${company} (${formattedStartDate(startDate)} - ${
                        endDate ? formattedEndDate(endDate) : "Present"
                    }) (${employmentType}) • ${getTotalDuration(startDate, endDate)}`}
                </p>
                <ul className="mt-2 list-inside list-disc">
                    {descriptions.map((desc, index) => (
                        <li key={index}>{desc}</li>
                    ))}
                </ul>
            </div>
        </motion.div>
    );
};
